import apiClient from "../../api/api";

/**
 * Retrieves a paginated list of coaching items.
 *
 * @param {Object} [params={}] - Query parameters used to filter and paginate coaching items.
 * @returns {Promise<Object>} The API response data.
 */
export const listCoachings = async (params = {}) => {
  const response = await apiClient.get("/growth/coachings", { params });
  return response.data;
};

/**
 * Retrieves coaching items assigned to a specific user.
 *
 * @param {number|string} userId - The user ID used as the coachee filter.
 * @param {number} [page=1] - The page number to retrieve.
 * @param {number} [limit=100] - The maximum number of records per page.
 * @returns {Promise<Object>} The API response data containing the user's coaching items.
 */
export const listUserCoachings = async (userId, page = 1, limit = 100) => {
  const response = await apiClient.get(
    `/growth/coachings?page=${page}&limit=${limit}&coacheeId=${userId}`
  );

  return response.data;
};

/**
 * Creates a new coaching item.
 *
 * @param {Object} coaching - The coaching payload to create.
 * @returns {Promise<Object>} The created coaching data returned by the API.
 */
export const addCoaching = async (coaching) => {
  const response = await apiClient.post("/growth/coachings", coaching);

  return response.data;
};

/**
 * Updates an existing coaching item.
 *
 * @param {number|string} coachingId - The ID of the coaching item to update.
 * @param {Object} coaching - The updated coaching payload.
 * @returns {Promise<Object>} The updated coaching data returned by the API.
 */
export const updateCoaching = async (coachingId, coaching) => {
  const response = await apiClient.put(
    `/growth/coachings/${coachingId}`,
    coaching
  );

  return response.data;
};

/**
 * Deletes a coaching item.
 *
 * @param {number|string} coachingId - The ID of the coaching item to delete.
 * @returns {Promise<Object>} The API response data.
 */
export const deleteCoaching = async (coachingId) => {
  const response = await apiClient.delete(
    `/growth/coachings/${coachingId}`
  );

  return response.data;
};

/**
 * Creates a new session for a coaching item.
 *
 * @param {number|string} coachingId - The ID of the coaching item.
 * @param {Object} session - The coaching session payload.
 * @returns {Promise<Object>} The created coaching session data.
 */
export const addCoachingSession = async (coachingId, session) => {
  const response = await apiClient.post(
    `growth/coachings/${coachingId}/sessions`,
    session
  );

  return response.data;
};

/**
 * Retrieves sessions associated with a coaching item.
 *
 * @param {number|string} coachingId - The ID of the coaching item.
 * @param {number} [page=1] - The page number to retrieve.
 * @param {number} [limit=10] - The maximum number of sessions per page.
 * @returns {Promise<Object>} The API response data containing coaching sessions.
 */
export const getCoachingSessions = async (
  coachingId,
  page = 1,
  limit = 10
) => {
  const response = await apiClient.get(
    `growth/coachings/${coachingId}/sessions`,
    {
      params: {
        page,
        limit,
      },
    }
  );

  return response.data;
};

/**
 * Retrieves a specific kudos entry by ID.
 *
 * @param {number|string} kudosId - The ID of the kudos entry.
 * @returns {Promise<Object>} The kudos data returned by the API.
 */
export const getKudosById = async (kudosId) => {
  const response = await apiClient.get(`growth/kudos/${kudosId}`);

  return response.data;
};

/**
 * Retrieves the latest kudos entries.
 *
 * The request currently limits the result to three records.
 *
 * @returns {Promise<Object>} The API response data containing kudos entries.
 */
export const getKudos = async (limit) => {
  const response = await apiClient.get(`growth/kudos?limit=${limit}`);

  return response.data;
};

/**
 * Creates a new kudos entry.
 *
 * @param {Object} kudos - The kudos payload to create.
 * @returns {Promise<Object>} The created kudos data returned by the API.
 */
export const addKudos = async (kudos) => {
  const response = await apiClient.post("growth/kudos", kudos);

  return response.data;
};

/**
 * Deletes a kudos entry.
 *
 * @param {number|string} kudosId - The ID of the kudos entry to delete.
 * @returns {Promise<Object>} The API response data.
 */
export const deleteKudos = async (kudosId) => {
  const response = await apiClient.delete(
    `growth/kudos/${kudosId}`
  );

  return response.data;
};

/**
 * Retrieves a specific blocker by ID.
 *
 * @param {number|string} blockerId - The ID of the blocker.
 * @returns {Promise<Object>} The blocker data returned by the API.
 */
export const getBlockersById = async (blockerId) => {
  const response = await apiClient.get(
    `growth/blockers/${blockerId}`
  );

  return response.data;
};

/**
 * Retrieves blockers using optional query parameters.
 *
 * @param {Object} [params={}] - Query parameters used to filter and paginate blockers.
 * @returns {Promise<Object>} The API response data containing blockers.
 */
export const getBlockers = async (params = {}) => {
  const response = await apiClient.get("/growth/blockers", {
    params,
  });

  return response.data;
};

/**
 * Creates a new blocker.
 *
 * @param {Object} blockers - The blocker payload to create.
 * @returns {Promise<Object>} The created blocker data returned by the API.
 */
export const addBlockers = async (blockers) => {
  const response = await apiClient.post(
    "growth/blockers",
    blockers
  );

  return response.data;
};

/**
 * Deletes a blocker.
 *
 * @param {number|string} blockerId - The ID of the blocker to delete.
 * @returns {Promise<Object>} The API response data.
 */
export const deleteBlockers = async (blockerId) => {
  const response = await apiClient.delete(
    `growth/blockers/${blockerId}`
  );

  return response.data;
};

/**
 * Updates an existing blocker.
 *
 * Changing the severity may cause the backend to recalculate the blocker's SLA.
 *
 * @param {number|string} id - The ID of the blocker to update.
 * @param {Object} payload - The blocker update payload.
 * @param {string} payload.title - The blocker title.
 * @param {string} payload.description - The blocker description.
 * @param {string} payload.severity - The blocker severity.
 * @param {number|string} payload.ownerId - The ID of the blocker owner.
 * @returns {Promise<Object>} The updated blocker data returned by the API.
 */
export const updateBlocker = async (id, payload) => {
  const response = await apiClient.put(
    `growth/blockers/${id}`,
    payload
  );

  return response.data;
};

/**
 * Creates a new anonymous feedback entry.
 *
 * @param {Object} feedback - The anonymous feedback payload.
 * @returns {Promise<Object>} The created anonymous feedback data.
 */
export const addAnonymousFeedback = async (feedback) => {
  const response = await apiClient.post(
    "/growth/anonymous-feedbacks",
    feedback
  );

  return response.data;
};

/**
 * Retrieves anonymous feedback entries.
 *
 * The request currently retrieves the first page with a maximum of 100 records.
 *
 * @returns {Promise<Object>} The API response data containing anonymous feedback entries.
 */
export const getAnonymousFeedbacks = async () => {
  const response = await apiClient.get(
    "growth/anonymous-feedbacks?page=1&limit=100"
  );

  return response.data;
};

/**
 * Updates an anonymous feedback entry.
 *
 * @param {number|string} feedbackId - The ID of the feedback entry to update.
 * @param {Object} feedback - The updated feedback payload.
 * @returns {Promise<Object>} The updated anonymous feedback data returned by the API.
 */
export const updateAnonymousFeedback = async (
  feedbackId,
  feedback
) => {
  const response = await apiClient.put(
    `/growth/anonymous-feedbacks/${feedbackId}`,
    feedback
  );

  return response.data;
};

/**
 * Updates only the status of an anonymous feedback entry.
 *
 * @param {number|string} feedbackId - The ID of the feedback entry.
 * @param {string} status - The new feedback status.
 * @returns {Promise<Object>} The updated anonymous feedback data returned by the API.
 */
export const updateAnonymousFeedbackStatus = async (feedbackId,status) => {
  const response = await apiClient.patch(`/growth/anonymous-feedbacks/${feedbackId}`,{status}, {successToast: `Feedback status successfully updated to ${status.replaceAll("-", " ").replace(/\b\w/g, char => char.toUpperCase())}.`});

  return response.data;
};